/**
 * Client-side built-in operations
 * Matches DiscoveryOperation format from Azure API for consistency
 * Based on LogicAppsUX patterns
 */

import type { DiscoveryOperation, BuiltInOperation } from '@/types/operations';

// Control Operations
export const foreachOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'foreach',
  name: 'foreach',
  type: 'Foreach',
  properties: {
    api: {
      id: 'connectionProviders/control',
      name: 'control',
      displayName: 'Control',
      brandColor: '#8C3900',
      description: 'Control operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/control.svg',
    },
    summary: 'For each',
    description: 'Executes a block of actions for each item in the input array.',
    visibility: 'Important',
    operationType: 'Foreach',
    brandColor: '#486991',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/foreach.svg',
  },
};

export const untilOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'until',
  name: 'until',
  type: 'Until',
  properties: {
    api: {
      id: 'connectionProviders/control',
      name: 'control',
      displayName: 'Control',
      brandColor: '#8C3900',
      description: 'Control operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/control.svg',
    },
    summary: 'Until',
    description: 'Executes a block of actions until a specified condition evaluates to true.',
    visibility: 'Important',
    operationType: 'Until',
    brandColor: '#486991',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/until.svg',
  },
};

export const scopeOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'scope',
  name: 'scope',
  type: 'Scope',
  properties: {
    api: {
      id: 'connectionProviders/control',
      name: 'control',
      displayName: 'Control',
      brandColor: '#8C3900',
      description: 'Control operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/control.svg',
    },
    summary: 'Scope',
    description: 'Encapsulate a block of actions and inherit the last terminal status (Succeeded, Failed, Cancelled) of actions inside.',
    visibility: 'Important',
    operationType: 'Scope',
    brandColor: '#8C3900',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/scope.svg',
  },
};

export const ifOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'if',
  name: 'if',
  type: 'If',
  properties: {
    api: {
      id: 'connectionProviders/control',
      name: 'control',
      displayName: 'Control',
      brandColor: '#8C3900',
      description: 'Control operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/control.svg',
    },
    summary: 'Condition',
    description: 'Identifies which block of actions to execute based on the evaluation of condition input.',
    visibility: 'Important',
    operationType: 'If',
    brandColor: '#484F58',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/condition.svg',
  },
};

export const switchOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'switch',
  name: 'switch',
  type: 'Switch',
  properties: {
    api: {
      id: 'connectionProviders/control',
      name: 'control',
      displayName: 'Control',
      brandColor: '#8C3900',
      description: 'Control operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/control.svg',
    },
    summary: 'Switch',
    description: 'Identifies a single case to execute based on the evaluation of switch input.',
    visibility: 'Important',
    operationType: 'Switch',
    brandColor: '#484F58',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/switch.svg',
  },
};

export const terminateOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'terminate',
  name: 'terminate',
  type: 'Terminate',
  properties: {
    api: {
      id: 'connectionProviders/control',
      name: 'control',
      displayName: 'Control',
      brandColor: '#8C3900',
      description: 'Control operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/control.svg',
    },
    summary: 'Terminate',
    description: 'Terminate the execution of a Logic App run.',
    visibility: 'Important',
    operationType: 'Terminate',
    brandColor: '#F41700',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/terminate.svg',
  },
};

// Variable Operations
export const initializeVariableOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'initializevariable',
  name: 'initializevariable',
  type: 'InitializeVariable',
  properties: {
    api: {
      id: 'connectionProviders/variable',
      name: 'variable',
      displayName: 'Variables',
      brandColor: '#770BD6',
      description: 'All variable operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/variable.svg',
    },
    summary: 'Initialize variable',
    description: 'Create and initialize a new variable.',
    visibility: 'Important',
    operationType: 'InitializeVariable',
    brandColor: '#770BD6',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/variable.svg',
  },
};

export const setVariableOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'setvariable',
  name: 'setvariable',
  type: 'SetVariable',
  properties: {
    api: {
      id: 'connectionProviders/variable',
      name: 'variable',
      displayName: 'Variables',
      brandColor: '#770BD6',
      description: 'All variable operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/variable.svg',
    },
    summary: 'Set variable',
    description: 'Set the value of a variable.',
    visibility: 'Important',
    operationType: 'SetVariable',
    brandColor: '#770BD6',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/variable.svg',
  },
};

export const incrementVariableOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'incrementvariable',
  name: 'incrementvariable',
  type: 'IncrementVariable',
  properties: {
    api: {
      id: 'connectionProviders/variable',
      name: 'variable',
      displayName: 'Variables',
      brandColor: '#770BD6',
      description: 'All variable operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/variable.svg',
    },
    summary: 'Increment variable',
    description: 'Increment the value of a numeric variable.',
    visibility: 'Important',
    operationType: 'IncrementVariable',
    brandColor: '#770BD6',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/variable.svg',
  },
};

export const decrementVariableOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'decrementvariable',
  name: 'decrementvariable',
  type: 'DecrementVariable',
  properties: {
    api: {
      id: 'connectionProviders/variable',
      name: 'variable',
      displayName: 'Variables',
      brandColor: '#770BD6',
      description: 'All variable operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/variable.svg',
    },
    summary: 'Decrement variable',
    description: 'Decrement the value of a numeric variable.',
    visibility: 'Important',
    operationType: 'DecrementVariable',
    brandColor: '#770BD6',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/variable.svg',
  },
};

export const appendStringVariableOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'appendtostringvariable',
  name: 'appendtostringvariable',
  type: 'AppendToStringVariable',
  properties: {
    api: {
      id: 'connectionProviders/variable',
      name: 'variable',
      displayName: 'Variables',
      brandColor: '#770BD6',
      description: 'All variable operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/variable.svg',
    },
    summary: 'Append to string variable',
    description: 'Append text to a string variable.',
    visibility: 'Important',
    operationType: 'AppendToStringVariable',
    brandColor: '#770BD6',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/variable.svg',
  },
};

export const appendArrayVariableOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'appendtoarrayvariable',
  name: 'appendtoarrayvariable',
  type: 'AppendToArrayVariable',
  properties: {
    api: {
      id: 'connectionProviders/variable',
      name: 'variable',
      displayName: 'Variables',
      brandColor: '#770BD6',
      description: 'All variable operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/variable.svg',
    },
    summary: 'Append to array variable',
    description: 'Append an item to an array variable.',
    visibility: 'Important',
    operationType: 'AppendToArrayVariable',
    brandColor: '#770BD6',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/variable.svg',
  },
};

// Data Operations
export const composeOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'compose',
  name: 'compose',
  type: 'Compose',
  properties: {
    api: {
      id: 'connectionProviders/dataOperationNew',
      name: 'dataOperationNew',
      displayName: 'Data Operations',
      brandColor: '#8c6cff',
      description: 'Data Operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/compose.svg',
    },
    summary: 'Compose',
    description: 'Compose a value or transform data.',
    visibility: 'Important',
    operationType: 'Compose',
    brandColor: '#8c6cff',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/compose.svg',
  },
};

export const parseJsonOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'parsejson',
  name: 'parsejson',
  type: 'ParseJson',
  properties: {
    api: {
      id: 'connectionProviders/dataOperationNew',
      name: 'dataOperationNew',
      displayName: 'Data Operations',
      brandColor: '#8c6cff',
      description: 'Data Operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/compose.svg',
    },
    summary: 'Parse JSON',
    description: 'Parse JSON content into a structured object.',
    visibility: 'Important',
    operationType: 'ParseJson',
    brandColor: '#8c6cff',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/compose.svg',
  },
};

export const selectOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'select',
  name: 'select',
  type: 'Select',
  properties: {
    api: {
      id: 'connectionProviders/dataOperationNew',
      name: 'dataOperationNew',
      displayName: 'Data Operations',
      brandColor: '#8c6cff',
      description: 'Data Operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/compose.svg',
    },
    summary: 'Select',
    description: 'Transform arrays by mapping their elements.',
    visibility: 'Important',
    operationType: 'Select',
    brandColor: '#8c6cff',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/compose.svg',
  },
};

export const filterArrayOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'filterarray',
  name: 'filterarray',
  type: 'Query',
  properties: {
    api: {
      id: 'connectionProviders/dataOperationNew',
      name: 'dataOperationNew',
      displayName: 'Data Operations',
      brandColor: '#8c6cff',
      description: 'Data Operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/compose.svg',
    },
    summary: 'Filter array',
    description: 'Filter an array based on a condition.',
    visibility: 'Important',
    operationType: 'Query',
    brandColor: '#8c6cff',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/compose.svg',
  },
};

export const joinOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'join',
  name: 'join',
  type: 'Join',
  properties: {
    api: {
      id: 'connectionProviders/dataOperationNew',
      name: 'dataOperationNew',
      displayName: 'Data Operations',
      brandColor: '#8c6cff',
      description: 'Data Operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/compose.svg',
    },
    summary: 'Join',
    description: 'Join an array into a string with a delimiter.',
    visibility: 'Important',
    operationType: 'Join',
    brandColor: '#8c6cff',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/compose.svg',
  },
};

// HTTP Operations
export const httpActionOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'http',
  name: 'http',
  type: 'Http',
  properties: {
    api: {
      id: 'connectionProviders/http',
      name: 'http',
      displayName: 'HTTP',
      brandColor: '#709727',
      description: 'All Http operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/http.svg',
    },
    summary: 'HTTP',
    description: 'Make an HTTP request to any endpoint.',
    visibility: 'Important',
    operationType: 'Http',
    brandColor: '#709727',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/http.svg',
  },
};

export const httpWithSwaggerOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'httpWithSwagger',
  name: 'httpWithSwagger',
  type: 'HttpWithSwagger',
  properties: {
    api: {
      id: 'connectionProviders/http',
      name: 'http',
      displayName: 'HTTP',
      brandColor: '#709727',
      description: 'All Http operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/http.svg',
    },
    summary: 'HTTP + Swagger',
    description: 'Make an HTTP request to any endpoint described by a Swagger/OpenAPI definition.',
    visibility: 'Important',
    operationType: 'HttpWithSwagger',
    brandColor: '#709727',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/http.svg',
  },
};

export const httpWebhookOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'httpWebhook',
  name: 'httpWebhook',
  type: 'HttpWebhook',
  properties: {
    api: {
      id: 'connectionProviders/http',
      name: 'http',
      displayName: 'HTTP',
      brandColor: '#709727',
      description: 'All Http operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/http.svg',
    },
    summary: 'HTTP Webhook',
    description: 'Create a subscription to an endpoint with a callback URL.',
    visibility: 'Important',
    operationType: 'HttpWebhook',
    brandColor: '#709727',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/http.svg',
  },
};

export const responseOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'response',
  name: 'response',
  type: 'Response',
  properties: {
    api: {
      id: 'connectionProviders/request',
      name: 'request',
      displayName: 'Request',
      brandColor: '#009DA5',
      description: 'Operations to handle inbound request to workflow and send workflow response',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/request.svg',
    },
    summary: 'Response',
    description: 'Return a response to the caller.',
    visibility: 'Important',
    operationType: 'Response',
    brandColor: '#009DA5',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/request.svg',
  },
};

export const requestOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'manual',
  name: 'manual',
  type: 'Request',
  kind: 'Http',
  properties: {
    api: {
      id: 'connectionProviders/request',
      name: 'request',
      displayName: 'Request',
      brandColor: '#009DA5',
      description: 'Operations to handle inbound request to workflow and send workflow response',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/request.svg',
    },
    summary: 'When an HTTP request is received',
    description: 'Trigger the workflow when an HTTP request is received.',
    visibility: 'Important',
    operationType: 'Request',
    trigger: 'single',
    brandColor: '#009DA5',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/request.svg',
  },
};

// Agent Trigger (A2A - Agent-to-Agent communication)
export const a2aRequestOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'a2arequest',
  name: 'When_a_new_chat_session_starts',
  type: 'Request',
  kind: 'Agent',
  properties: {
    api: {
      id: 'connectionProviders/a2a',
      name: 'a2aRequest',
      brandColor: '#709727',
      description: 'Agent Request operations for chat-based workflows',
      displayName: 'Agent Request',
      iconUri: 'data:image/svg+xml;base64,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',
    },
    summary: 'When a new chat session starts',
    description: 'Triggers the flow when a chat session is initiated for conversational agent workflows.',
    visibility: 'Important',
    operationType: 'Request',
    operationKind: 'Agent',
    trigger: 'single',
    brandColor: '#709727',
    iconUri: 'data:image/svg+xml;base64,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',
  },
};

// Schedule Operations
export const recurrenceOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'recurrence',
  name: 'recurrence',
  type: 'Recurrence',
  properties: {
    api: {
      id: 'connectionProviders/schedule',
      name: 'schedule',
      displayName: 'Schedule',
      brandColor: '#1F85FF',
      description: 'Schedule operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/recurrence.svg',
    },
    summary: 'Recurrence',
    description: 'Trigger the workflow on a recurring schedule.',
    visibility: 'Important',
    operationType: 'Recurrence',
    trigger: 'batch',
    brandColor: '#1F85FF',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/recurrence.svg',
  },
};

export const slidingWindowOperation: DiscoveryOperation<BuiltInOperation>= {
  name: 'slidingwindow',
  id: 'slidingwindow',
  type: 'SlidingWindow',
  properties: {
    api: {
      id: 'connectionProviders/schedule',
      name: 'schedule',
      brandColor: '#1F85FF',
      description: 'Schedule operations',
      displayName: 'Schedule',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/recurrence.svg',
    },
    capabilities: ['Stateful'],
    summary: 'Sliding Window',
    description: 'Triggers a series of fixed-sized, non-overlapping, and contiguous time intervals from a specified start time.',
    visibility: 'Important',
    trigger: 'single',
    operationType: 'SlidingWindow',
    brandColor: '#1F85FF',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/recurrence.svg',
  },
};

export const delayOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'delay',
  name: 'delay',
  type: 'Wait',
  properties: {
    api: {
      id: 'connectionProviders/schedule',
      name: 'schedule',
      displayName: 'Schedule',
      brandColor: '#1F85FF',
      description: 'Schedule operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/recurrence.svg',
    },
    summary: 'Delay',
    description: 'Pause the workflow for a specified duration.',
    visibility: 'Important',
    operationType: 'Wait',
    brandColor: '#1F85FF',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/wait.svg',
  },
};

export const delayUntilOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'delayuntil',
  name: 'delayuntil',
  type: 'Wait',
  properties: {
    api: {
      id: 'connectionProviders/schedule',
      name: 'schedule',
      displayName: 'Schedule',
      brandColor: '#1F85FF',
      description: 'Schedule operations',
      iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/recurrence.svg',
    },
    summary: 'Delay until',
    description: 'Pause the workflow until a specified timestamp.',
    visibility: 'Important',
    operationType: 'Wait',
    brandColor: '#1F85FF',
    iconUri: 'https://logicappsv2resources.blob.core.windows.net/icons/wait_until.svg',
  },
};

// Agent Operations
export const agentLoopOperation: DiscoveryOperation<BuiltInOperation> = {
  id: 'agentloop',
  name: 'agentloop',
  type: 'Agent',
  properties: {
    api: {
      id: 'connectionProviders/agent',
      name: 'agent',
      displayName: 'AI agent',
      brandColor: '#3352B9',
      description: 'AI agent operations',
      iconUri: 'data:image/svg+xml;base64,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',
    },
    summary: 'Agent',
    description: 'Loop in which the AI agent decides at each step which tools to use and how, and which text to generate to respond to the user.',
    visibility: 'Important',
    operationType: 'Agent',
    brandColor: '#3352B9',
    iconUri: 'data:image/svg+xml;base64,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',
  },
};

/**
 * Get all client-side built-in operations
 * These are guaranteed to be available even without API calls
 */
export function getClientBuiltInOperations(): DiscoveryOperation<BuiltInOperation>[] {
  return [
    // Triggers
    requestOperation,
    // a2aRequestOperation excluded — conversational agent workflows hidden in Otto
    recurrenceOperation,
    // Note: SlidingWindow removed - consumption-only, not supported in Otto/Edge
    // Control
    ifOperation,
    switchOperation,
    foreachOperation,
    untilOperation,
    scopeOperation,
    terminateOperation,
    // Variables
    initializeVariableOperation,
    setVariableOperation,
    incrementVariableOperation,
    decrementVariableOperation,
    appendStringVariableOperation,
    appendArrayVariableOperation,
    // Data Operations
    composeOperation,
    parseJsonOperation,
    selectOperation,
    filterArrayOperation,
    joinOperation,
    // HTTP & Request
    httpActionOperation,
    httpWithSwaggerOperation,
    httpWebhookOperation,
    responseOperation,
    // Schedule
    delayOperation,
    delayUntilOperation,
    // Agent
    agentLoopOperation,
  ];
}
